import React, { Fragment, useEffect, useState } from 'react'
import { fetchSupportUsers } from '../../hooks/fetch-hooks/fetch-all-support-users/get-all-support-users'
import { fetchLsqAdminUsers } from '../../hooks/fetch-hooks/fetch-lsq-admin-users/fetch-lsq-admin-users'
import { fetchLsqAdminUserById } from '../../hooks/fetch-hooks/fetch-lsq-admin-user-by-id/fetch-lsq-admin-user-by-id'
import { flipSupportActiveUser, toggleLsqUserCheckedIn, lsqRowId } from './helper';
import DistributeLeadModal from '../../components/support/DistributeLeadModal';
import LeadTransferTab from './components/LeadTransferTab';
import { useAddModalMain } from "../../contexts/address-modal/AddModal";
import { useMutation, useQueryClient } from "react-query";

const tabButtonClass = (isActive) => `px-4 py-2 font-sans text-sm font-medium border-b-2 -mb-px transition-colors ${isActive
    ? 'border-[#00A48B] text-[#00A48B]'
    : 'border-transparent text-gray-500 hover:text-gray-700'
    }`;

const Support = () => {
    const urlParams = new URLSearchParams(location.search);
    const page = parseInt(urlParams.get('page')) >= 1 ? parseInt(urlParams.get('page')) : 1;
    const pageSize = Number.parseInt(urlParams.get('size'), 10) || 50;

    const [activeTab, setActiveTab] = useState('support');
    const [selectedLsqUserId, setSelectedLsqUserId] = useState(null);

    const { data: supportUsers, refetch } = fetchSupportUsers();
    const { data: lsqUsersResponse, isLoading: lsqLoading, isError: lsqError, refetch: refetchLsq } = fetchLsqAdminUsers(activeTab === 'lsq' || activeTab === 'transfer');
    const queryClient = useQueryClient();

    const lsqDetailEnabled = activeTab === 'lsq' && Boolean(selectedLsqUserId);
    const {
        data: lsqUserDetailResponse,
        isLoading: lsqDetailLoading,
        isError: lsqDetailError,
    } = fetchLsqAdminUserById(selectedLsqUserId, lsqDetailEnabled);

    const lsqToggleMutation = useMutation(
        (userId) => toggleLsqUserCheckedIn(userId),
        {
            onSuccess: (result) => {
                if (result?.userId) {
                    queryClient.invalidateQueries(['lsq-admin-user', result.userId]);
                }
            },
        }
    );

    const [currentPage, setCurrentPage] = useState(page);
    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const { AddopenModal, AddcloseModal } = useAddModalMain();

    const lsqRows = lsqUsersResponse?.data?.data;
    const lsqList = Array.isArray(lsqRows) ? lsqRows : [];
    const lsqDetail = lsqUserDetailResponse?.data?.data;
    const lsqDetailFailed = lsqDetailEnabled && !lsqDetailLoading && (
        lsqDetailError || lsqUserDetailResponse?.data?.success === false
    );

    useEffect(() => {
        if (activeTab !== 'lsq') {
            setSelectedLsqUserId(null);
        }
    }, [activeTab]);

    const distributeClickHandler = (original) => {
        AddopenModal(() => <DistributeLeadModal row={original} supportUsers={supportUsers?.data?.data?.rows?.filter((user) => user.id !== original.id && user.userDetails.isActive)} refetch={refetch} closeModal={AddcloseModal} />)
    }

    const handleToggleActive = async (id) => {
        if (!id) return;

        try {
            const response = await flipSupportActiveUser(id);
            if (response) {
                refetch();
            }
        } catch (error) {
            console.log(error);
        }
    }

    const handleLsqToggle = (userId) => {
        if (!userId || lsqToggleMutation.isLoading) return;
        lsqToggleMutation.mutate(userId);
    }

    const toggleLsqRow = (id) => {
        if (!id) return;
        setSelectedLsqUserId((prev) => (prev === id ? null : id));
    }

    useEffect(() => {
        const updateUrl = () => {
            const url = new URL(location);
            url.searchParams.set('page', currentPage);
            url.searchParams.set('size', pageSize);
            history.pushState({}, '', url);
        };
        updateUrl();
        refetch();
    }, [currentPage, history, location]);

    useEffect(() => {
        if (supportUsers?.data?.data) {
            setTableData(supportUsers.data.data.rows);
            setTotal(supportUsers.data.data.count);
        }
    }, [supportUsers]);

    const switchClass = "relative inline-flex gap-2 items-center cursor-pointer";
    const trackClass = "relative flex items-center justify-start peer-checked:justify-end p-[2px] w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:bg-[#00A48B]";
    const toggleBusy = lsqToggleMutation.isLoading && lsqToggleMutation.variables === selectedLsqUserId;

    return (
        <div className="p-7 h-full overflow-y-scroll bg-gray-50">
            <div className="flex gap-2 border-b border-gray-200 mb-4">
                <button
                    type="button"
                    onClick={() => setActiveTab('support')}
                    className={tabButtonClass(activeTab === 'support')}
                >
                    Support
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('lsq')}
                    className={tabButtonClass(activeTab === 'lsq')}
                >
                    LSQ
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('transfer')}
                    className={tabButtonClass(activeTab === 'transfer')}
                >
                    Lead Transfer
                </button>
            </div>

            {activeTab === 'support' && (
                <>
                    <div className="block bg-white rounded-2xl shadow-soft-xl w-[100%] bg-[white] mt-4 p-2 px-4">
                        <table className="table-auto w-[100%] font-sans">
                            <thead className="text-center border-b-2 border-gray-50 h-16">
                                <tr className="text-left border-b-2 border-gray-100">
                                    <th className="font-sans text-left pl-3">#</th>
                                    <th className="font-sans text-left pl-3">Name</th>
                                    <th className="font-sans text-left pl-3">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableData?.map((row) => (
                                    <tr key={row.id} className="hover:bg-white px-2 border-b text-left border-gray-50 pl-3 font-sans">
                                        <td className="py-4 text-left pl-3 font-sans cursor-pointer hover:text-black text-elm-500 font-medium">
                                            {row.id}
                                        </td>
                                        <td className="pl-3 py-4 text-left text-lg font-sans">
                                            <h1>
                                                <b className="text-lg">
                                                    {row.name} : &nbsp;
                                                </b>
                                                <span className="text-elm-500 text-lg">
                                                    {row.contact_no}
                                                </span>
                                            </h1>
                                        </td>
                                        <td className="pl-3 py-4 text-left font-sans">

                                            <div className="w-full py-2 h-full flex flex-col gap-1 justify-evenly">
                                                <label className={switchClass}>
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only peer border-none outline-none"
                                                        checked={row?.userDetails?.isActive}
                                                        onChange={() => {
                                                            handleToggleActive(row?.id);
                                                        }}
                                                    />
                                                    <div className={trackClass}>
                                                        <div className="absolute bg-white border border-gray-300 rounded-full h-5 w-5 transition-all"></div>
                                                    </div>
                                                    <button
                                                        onClick={() => distributeClickHandler(row)}
                                                        disabled={row?.userDetails?.isActive}
                                                        className={`mt-2 px-3 py-1 text-sm rounded-md ${!row?.userDetails?.isActive
                                                            ? 'bg-[#00A48B] text-white-50 hover:bg-[#008F79]'
                                                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                            }`}
                                                    >
                                                        Distribute
                                                    </button>
                                                </label>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="btn_div">
                        <button disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)} className="previous">Previous</button>
                        <span className="pageindex">Page {currentPage} of {Math.ceil(total / pageSize)}</span>
                        <button disabled={currentPage >= Math.ceil(total / pageSize)} onClick={() => setCurrentPage(currentPage + 1)} className="next">Next</button>
                    </div>
                </>
            )}

            {activeTab === 'lsq' && (
                <div className="block bg-white rounded-2xl shadow-soft-xl w-[100%] bg-[white] mt-4 p-2 px-4">
                        <div className="flex justify-end mb-2">
                            <button
                                type="button"
                                onClick={() => refetchLsq()}
                                disabled={lsqLoading}
                                className="text-sm px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-800 disabled:opacity-50"
                            >
                                Refresh
                            </button>
                        </div>
                        {lsqLoading && (
                            <p className="font-sans text-gray-600 py-6 pl-3">Loading LeadSquared users…</p>
                        )}
                        {lsqError && !lsqLoading && (
                            <p className="font-sans text-red-600 py-6 pl-3">Could not load LeadSquared users.</p>
                        )}
                        {!lsqLoading && !lsqError && lsqUsersResponse?.data?.success === false && (
                            <p className="font-sans text-red-600 py-6 pl-3">
                                {typeof lsqUsersResponse?.data?.error === 'string' ? lsqUsersResponse.data.error : 'Could not load LeadSquared users.'}
                            </p>
                        )}
                        {!lsqLoading && !lsqError && lsqUsersResponse?.data?.success !== false && (
                            <table className="table-auto w-[100%] font-sans">
                                <thead className="text-center border-b-2 border-gray-50 h-16">
                                    <tr className="text-left border-b-2 border-gray-100">
                                        <th className="font-sans text-left pl-3">#</th>
                                        <th className="font-sans text-left pl-3">Name</th>
                                        <th className="font-sans text-left pl-3">Email</th>
                                        <th className="font-sans text-left pl-3">Role</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lsqList.map((row, idx) => {
                                        const id = lsqRowId(row);
                                        const name = [row.FirstName, row.LastName].filter(Boolean).join(' ').trim() || '—';
                                        const expanded = Boolean(id && selectedLsqUserId === id);
                                        return (
                                            <Fragment key={id || `row-${idx}`}>
                                                <tr className={`border-b text-left border-gray-50 pl-3 font-sans ${expanded ? 'bg-emerald-50/50' : 'hover:bg-white'}`}>
                                                    <td className="py-4 text-left pl-3 font-sans text-elm-500 font-medium align-top">{idx + 1}</td>
                                                    <td className="pl-3 py-4 text-left font-sans align-top">
                                                        {id ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleLsqRow(id)}
                                                                className="text-left font-sans text-[#00A48B] hover:underline font-medium inline-flex items-center gap-2 max-w-full"
                                                                aria-expanded={expanded}
                                                            >
                                                                <span className="text-gray-500 text-xs w-4 shrink-0 select-none" aria-hidden>
                                                                    {expanded ? '▼' : '▶'}
                                                                </span>
                                                                <span>{name}</span>
                                                            </button>
                                                        ) : (
                                                            <span>{name}</span>
                                                        )}
                                                    </td>
                                                    <td className="pl-3 py-4 text-left font-sans text-gray-700 align-top">{row.EmailAddress || '—'}</td>
                                                    <td className="pl-3 py-4 text-left font-sans text-gray-700 align-top">{row.Role || '—'}</td>
                                                </tr>
                                                {expanded && (
                                                    <tr className="border-b border-gray-100 bg-slate-50/90 font-sans">
                                                        <td colSpan={4} className="p-0">
                                                            <div className="border-l-4 border-[#00A48B] mx-3 my-2 mb-3 pl-4 pr-3 py-4 bg-white rounded-r-lg shadow-sm">
                                                                <div className="flex justify-between items-center gap-2 mb-3">
                                                                    <h3 className="text-sm font-semibold text-gray-800">LeadSquared details</h3>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setSelectedLsqUserId(null)}
                                                                        className="text-xs text-gray-600 hover:text-gray-900"
                                                                    >
                                                                        Collapse
                                                                    </button>
                                                                </div>
                                                                {lsqDetailLoading && (
                                                                    <p className="text-sm text-gray-600">Loading user details…</p>
                                                                )}
                                                                {lsqDetailFailed && !lsqDetailLoading && (
                                                                    <p className="text-sm text-red-600">
                                                                        {typeof lsqUserDetailResponse?.data?.error === 'string'
                                                                            ? lsqUserDetailResponse.data.error
                                                                            : 'Could not load this user from LeadSquared.'}
                                                                    </p>
                                                                )}
                                                                {!lsqDetailLoading && !lsqDetailFailed && lsqDetail && (
                                                                    <div className="space-y-4">
                                                                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                                                            <div><dt className="text-gray-500">User ID</dt><dd className="text-gray-900 break-all">{lsqDetail.UserId ?? lsqDetail.ID ?? selectedLsqUserId}</dd></div>
                                                                            <div><dt className="text-gray-500">Email</dt><dd className="text-gray-900">{lsqDetail.EmailAddress ?? '—'}</dd></div>
                                                                            <div><dt className="text-gray-500">Name</dt><dd className="text-gray-900">{[lsqDetail.FirstName, lsqDetail.LastName].filter(Boolean).join(' ').trim() || '—'}</dd></div>
                                                                            <div><dt className="text-gray-500">Role</dt><dd className="text-gray-900">{lsqDetail.Role ?? '—'}</dd></div>
                                                                            {lsqDetail.AvailabilityStatus != null && (
                                                                                <div><dt className="text-gray-500">Availability</dt><dd className="text-gray-900">{String(lsqDetail.AvailabilityStatus)}</dd></div>
                                                                            )}
                                                                        </dl>
                                                                        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100">
                                                                            <span className="text-sm text-gray-700">Checked in (LeadSquared)</span>
                                                                            <label className={`${switchClass} ${toggleBusy ? 'opacity-50 pointer-events-none' : ''}`}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="sr-only peer border-none outline-none"
                                                                                    checked={lsqDetail.IsCheckedIn === true}
                                                                                    disabled={toggleBusy}
                                                                                    onChange={() => handleLsqToggle(selectedLsqUserId)}
                                                                                />
                                                                                <div className={trackClass}>
                                                                                    <div className="absolute bg-white border border-gray-300 rounded-full h-5 w-5 transition-all"></div>
                                                                                </div>
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                        {!lsqLoading && !lsqError && lsqList.length === 0 && lsqUsersResponse?.data?.success !== false && (
                            <p className="font-sans text-gray-600 py-6 pl-3">No LeadSquared users returned.</p>
                        )}
                    </div>
            )}

            {activeTab === 'transfer' && (
                <div className="block bg-white rounded-2xl shadow-soft-xl w-[100%] mt-4 p-2 px-4">
                    <LeadTransferTab
                        lsqUsers={lsqList}
                        lsqLoading={lsqLoading}
                        lsqError={lsqError}
                        isActive={activeTab === 'transfer'}
                    />
                </div>
            )}
        </div>
    )
}

export default Support
